// Captura de assinatura na tela (toque ou mouse), com limpar/refazer. Exporta PNG (data URL) via onChange.
// Sem dependência nova — canvas + pointer events. Usado no fluxo de confirmação do lançamento de caixas.
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

type Stroke = { x: number; y: number }[];

interface BoxSignaturePadProps {
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  height?: number;
}

export function BoxSignaturePad({ onChange, disabled, height = 180 }: BoxSignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const drawingRef = useRef<Stroke | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (const point of stroke.slice(1)) ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    ctx.restore();
  }, []);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    redraw();
  }, [height, redraw]);

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const emit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const empty = strokesRef.current.length === 0;
    setIsEmpty(empty);
    onChange(empty ? null : canvas.toDataURL("image/png"));
  };

  const handleDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = [pointFromEvent(e)];
    strokesRef.current.push(drawingRef.current);
  };

  const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return;
    drawingRef.current.push(pointFromEvent(e));
    redraw();
  };

  const handleUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = null;
    emit();
  };

  const clear = () => {
    strokesRef.current = [];
    drawingRef.current = null;
    redraw();
    emit();
  };

  const undo = () => {
    strokesRef.current.pop();
    drawingRef.current = null;
    redraw();
    emit();
  };

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-input bg-background">
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, touchAction: "none" }}
          className="rounded-lg"
          onPointerDown={handleDown}
          onPointerMove={handleMove}
          onPointerUp={handleUp}
          onPointerLeave={handleUp}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {isEmpty ? "Assine no quadro acima (toque ou mouse)." : "Assinatura capturada."}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={undo} disabled={disabled || isEmpty}>
            Desfazer
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={clear} disabled={disabled || isEmpty}>
            <Eraser className="mr-1 h-4 w-4" /> Limpar
          </Button>
        </div>
      </div>
    </div>
  );
}
